import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { useAppState } from '../state/AppState';
import { Location } from '../services/types';
import { getNextScreenFrom, getScreenPath } from '../utils/smartNavigation';

// Extended location type for availability locations
interface AvailabilityLocation extends Location {
  isTelehealth?: boolean;
  address?: string;
  licenseKey?: string;
}

/**
 * ScreenI - Location selection screen.
 *
 * Allows the user to select where they would like to be seen.
 */
interface MapLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address: string;
}

// Center the map on Seattle by default
const DEFAULT_CENTER = { lat: 47.6062, lng: -122.3321 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '320px', borderRadius: 8 };

/** GTM dataLayer push — consumed by GTM tags (e.g. GA4 events). Mirrors ScreenP. */
function pushGoogleMapsErrorToDataLayer(code: string, detail?: string) {
  if (typeof window === 'undefined') return;
  const dl = (window as any).dataLayer;
  if (!dl?.push) return;
  dl.push({
    event: 'google_maps_error',
    maps_error_code: code,
    maps_error_detail: detail ?? '',
  });
}

const ScreenI: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const [mapLocations, setMapLocations] = useState<MapLocation[]>([]);
  const [availabilityLocations, setAvailabilityLocations] = useState<AvailabilityLocation[]>([]);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [activeInfoWindow, setActiveInfoWindow] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we have availabilities but no availableLocations, we're still processing
    if (state.availabilities && state.availabilities.length > 0 && 
        (!state.availableLocations || state.availableLocations.length === 0)) {
      setLoading(true);
      return;
    }
    
    // If we don't have availabilities yet, we're still loading
    if (!state.availabilities || state.availabilities.length === 0) {
      setLoading(true);
      return;
    }
    
    // If we have both, we can proceed
    if (state.availableLocations && state.availableLocations.length > 0) {
      try {
        // Get physical locations from availableLocations (with map coordinates)
        const physicalLocations = state.availableLocations.filter(loc => 
          !loc.isTelehealth && loc.lat && loc.lng
        );
        const transformedLocations: MapLocation[] = physicalLocations.map(loc => ({
          id: loc.id,
          name: loc.name,
          lat: loc.lat!,
          lng: loc.lng!,
          address: loc.address
        }));
        
        setMapLocations(transformedLocations);
        
        // Use available locations from state (processed by availabilitiesService)
        setAvailabilityLocations(state.availableLocations);
        setLoading(false);
        
      } catch (error) {
        setMapLocations([]);
        setLoading(false);
      }
    }
  }, [state.availableLocations, state.availabilities]);

  // Update map bounds when locations change
  useEffect(() => {
    if (map && mapLocations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      mapLocations.forEach((location) => {
        bounds.extend({ lat: location.lat, lng: location.lng });
      });
      
      // Fit map to bounds with padding
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
      
      // Limit zoom level after fitting bounds
      google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom() || 11;
        // Limit zoom to prevent being too close (minimum zoom level 10, maximum 15)
        const limitedZoom = Math.max(10, Math.min(currentZoom, 15));
        map.setZoom(limitedZoom);
      });
    }
  }, [map, mapLocations]);

  // Google Maps API loader
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    // Only track if the map section is relevant on this screen.
    if (mapLocations.length === 0) return;
    if (!loadError) return;

    const message = loadError instanceof Error ? loadError.message : String(loadError);
    pushGoogleMapsErrorToDataLayer('loader_failed', message);
  }, [loadError, mapLocations.length]);

  useEffect(() => {
    // Google sets this global on auth / billing / key-restriction issues.
    const w = window as any;
    const previous = w.gm_authFailure;

    w.gm_authFailure = () => {
      pushGoogleMapsErrorToDataLayer('gm_authFailure');
      if (typeof previous === 'function') previous();
    };

    return () => {
      w.gm_authFailure = previous;
    };
  }, []);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
    
    // Only fit bounds if we have locations
    if (mapLocations.length > 0) {
      // Create bounds to include all markers
      const bounds = new window.google.maps.LatLngBounds();
      mapLocations.forEach((location) => {
        bounds.extend({ lat: location.lat, lng: location.lng });
      });
      
      // Fit map to bounds with padding
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
      
      // Add some padding to the bounds and limit zoom level
      google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom() || 11;
        // Limit zoom to prevent being too close (minimum zoom level 10, maximum 15)
        const limitedZoom = Math.max(10, Math.min(currentZoom, 15));
        map.setZoom(limitedZoom);
      });
    }
  }, [mapLocations]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMarkerClick = (location: MapLocation) => {
    setActiveInfoWindow(activeInfoWindow === location.id ? null : location.id);
  };

  const handleInfoWindowClose = () => {
    setActiveInfoWindow(null);
  };

  const handleLocationConfirm = (location: MapLocation) => {
    dispatch({ type: 'SET_LOCATION', payload: location.name });
    dispatch({ type: 'SET_LOCATION_CODE', payload: location.id });
    dispatch({ type: 'SET_LOCATION_ADDRESS', payload: location.address || '' });
    
    // Find the full location data to get the licenseKey
    const fullLocationData = availabilityLocations.find(loc => loc.id === location.id);
    if (fullLocationData && fullLocationData.licenseKey) {
      dispatch({ type: 'SET_LICENSE_KEY', payload: parseInt(fullLocationData.licenseKey) });
    }
    
    // Use smart navigation to find next incomplete screen
    const nextScreen = getNextScreenFrom(state, 'i');
    const path = getScreenPath(nextScreen);
    
    navigate(path);
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card">
            <h2 className="mtg-screen-question mb-18">
              Where would you<br />like to be seen?
            </h2>

            {loading ? (
              <div style={{ margin: '32px 0', color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">
                Loading locations...
                <div style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                  Please wait while we fetch available locations for your area.
                </div>
              </div>
            ) : (
            <div className="button-container">
              <div role="listbox" aria-label="Location selection">
                  {availabilityLocations.map((location) => {
                    const isTelehealth = location.isTelehealth || false;
                    return (
                <button 
                        key={location.id} 
                  className="pill-btn-standard mb-24" 
                        onClick={() => { 
                          dispatch({ type: 'SET_LOCATION', payload: isTelehealth ? 'Telehealth' : location.name }); 
                          dispatch({ type: 'SET_LOCATION_CODE', payload: isTelehealth ? 'telehealth' : location.id }); 
                          // Save address for physical locations, empty for telehealth
                          dispatch({ type: 'SET_LOCATION_ADDRESS', payload: isTelehealth ? '' : (location.address || '') }); 
                          
                          // Set licenseKey for physical locations (not for telehealth)
                          if (!isTelehealth && location.licenseKey) {
                            dispatch({ type: 'SET_LICENSE_KEY', payload: parseInt(location.licenseKey) });
                          }
                          
                          // Use smart navigation to find next incomplete screen
                          const nextScreen = getNextScreenFrom(state, 'i');
                          const path = getScreenPath(nextScreen);
                          
                          navigate(path);
                        }}
                  role="option"
                  aria-selected="false"
                        aria-label={`Select ${isTelehealth ? 'Telehealth' : location.name} location`}
                        style={isTelehealth ? {} : { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
                      >
                        {isTelehealth ? (
                          'Telehealth'
                        ) : (
                          <>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      In-Person
                    </div>
                    <div style={{ fontSize: '0.9rem', fontStyle: 'italic', fontWeight: 100 }}>
                              {location.name}
                    </div>
                          </>
                        )}
                  </button>
                    );
                  })}
              </div>
            </div>
            )}
            
            {/* Map Section - Only show if there are physical locations */}
            {mapLocations.length > 0 && (
            <div className="mb-18" style={{ width: '100%', margin: '18px auto', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <style>
                {`
                  .gm-style .gm-style-iw-c {
                    max-width: 200px !important;
                  }
                  .gm-style .gm-style-iw-d {
                    overflow: hidden !important;
                  }
                  .gm-control-active {
                    background-color: #fff !important;
                    border: 1px solid #ccc !important;
                  }
                  .gm-control-active:hover {
                    background-color: #f5f5f5 !important;
                  }
                `}
              </style>
              {loadError ? (
                <div style={{ width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b5b5b5', borderRadius: 8 }}>
                  Map unavailable right now.
                </div>
              ) : isLoaded ? (
                <GoogleMap
                  key={`map-${mapLocations.length}`}
                  mapContainerStyle={{ ...MAP_CONTAINER_STYLE, borderRadius: 8 }}
                  center={DEFAULT_CENTER}
                  zoom={11}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: false,
                    styles: [
                      {
                        featureType: 'all',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#333333' }]
                      }
                    ]
                  }}
                >
                  {mapLocations.map((loc) => (
                    <React.Fragment key={loc.id}>
                      <Marker
                        position={{ lat: loc.lat, lng: loc.lng }}
                        icon={{
                          url: '/logo192.png',
                          scaledSize: new window.google.maps.Size(40, 40),
                        }}
                        onClick={() => handleMarkerClick(loc)}
                        title={loc.name}
                      />
                      {activeInfoWindow === loc.id && (
                        <InfoWindow
                          position={{ lat: loc.lat, lng: loc.lng }}
                          options={{
                            pixelOffset: new window.google.maps.Size(0, -30),
                          }}
                          onCloseClick={handleInfoWindowClose}
                        >
                          <div style={{ padding: '8px', minWidth: '120px' }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                              {loc.name}
                            </h3>
                            <p className="text-soft" style={{ margin: '0 0 8px 0', fontSize: '12px' }}>
                              {loc.address}
                            </p>
                            <button
                              onClick={() => handleLocationConfirm(loc)}
                              style={{
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                width: '100%'
                              }}
                            >
                              Select Location
                            </button>
                          </div>
                        </InfoWindow>
                      )}
                    </React.Fragment>
                  ))}
                </GoogleMap>
              ) : (
                <div style={{ width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b5b5b5', borderRadius: 8 }}>
                  Loading map...
                </div>
              )}
            </div>
            )}
            
            <ProgressBar progress={45} />
          </div>
          <button
            className="mtg-secondary-action-btn mb-18"
            onClick={() => navigate('/v')}
            aria-label="My option is not listed, contact us"
          >
            MY OPTION IS NOT LISTED
          </button>
        </main>
      </div>
    </div>
  );
};

export default ScreenI; 